/**
 * « Je rapporte des vidanges » — au checkout.
 *
 * Demande de Philippe (04/10/2026) : le client annonce les vidanges qu'il
 * rapportera au retrait, par type et quantité. Au comptoir on les recompte et on
 * valide ; la consigne est alors créditée sur ses FOLIES. Rien n'est déduit de
 * la commande : le montant affiché est une estimation, le recomptage fait foi.
 *
 * Les types viennent du serveur (`/shop-config` → `vidanges`) : les fiches
 * consigne réellement utilisées, avec deux produits en exemple, parce que le
 * nom de fiche est interne (« vidange cuisinable »). Liste vide = rien d'affiché.
 */
import { useEffect, useState } from 'react';
import { fetchShopConfigCached } from '../../hooks/ecommerce/useShopOrdering';
import type { TypeVidange } from '../../api/ecommerce.api';

const euros = (c: number) => (c / 100).toFixed(2).replace('.', ',') + ' €';

interface Props {
  value: Record<string, number>;
  onChange: (v: Record<string, number>) => void;
  /** « panier » : bloc compact, sous les articles (page panier, ticket bureau). */
  variante?: 'checkout' | 'panier';
}

export default function VidangesAnnonce({ value, onChange, variante = 'checkout' }: Props) {
  const compact = variante === 'panier';
  const [types, setTypes] = useState<TypeVidange[]>([]);
  const [ouvert, setOuvert] = useState(Object.values(value).some((q) => q > 0));

  useEffect(() => {
    let annule = false;
    fetchShopConfigCached()
      .then((c) => !annule && setTypes(c.vidanges || []))
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, []);

  if (types.length === 0) return null;

  const total = types.reduce((s, t) => s + (value[t.id] || 0) * t.price_cents, 0);
  const regler = (id: string, q: number) => onChange({ ...value, [id]: Math.max(0, Math.min(99, q)) });

  return (
    <div className={compact ? 'shop-sans rounded-xl border border-[#4a5c3a]/25 bg-white p-3' : 'mt-8 border-t-2 border-gray-200 pt-6'}>
      <label className={`flex items-start cursor-pointer group ${compact ? 'gap-3' : 'gap-4'}`}>
        <input
          type="checkbox"
          checked={ouvert}
          onChange={(e) => {
            setOuvert(e.target.checked);
            if (!e.target.checked) onChange({});
          }}
          className={`${compact ? 'mt-0.5 w-5 h-5' : 'mt-1 w-6 h-6'} text-green-700 border-2 border-gray-300 rounded focus:ring-green-500 cursor-pointer flex-shrink-0`}
        />
        <div>
          <div className={`${compact ? 'text-[15px]' : 'text-xl'} font-bold text-gray-900 group-hover:text-green-700 transition-colors`}>
            ♻️ Je rapporte des vidanges
          </div>
          <p className={`text-gray-600 mt-1 ${compact ? 'text-[13px] leading-snug' : ''}`}>
            Bouteilles et bocaux consignés : apportez-les au retrait. Nous les recomptons avec vous et la consigne
            est créditée sur vos FOLIES.
          </p>
        </div>
      </label>

      {ouvert && (
        <div className={compact ? 'mt-3 space-y-2' : 'mt-4 ml-0 sm:ml-10 space-y-2'}>
          {types.map((t) => {
            const q = value[t.id] || 0;
            return (
              <div
                key={t.id}
                className={`flex items-center gap-3 rounded-xl border-2 px-3 py-2 ${q > 0 ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
              >
                <div className="min-w-0 flex-1">
                  <div className={`font-semibold text-gray-900 ${compact ? 'text-[13.5px] leading-tight' : ''}`}>{t.name}</div>
                  <div className={`${compact ? 'text-[12px]' : 'text-sm'} text-gray-600 truncate`}>
                    {euros(t.price_cents)} la vidange{t.exemples.length > 0 && ` · ex. ${t.exemples.join(', ')}`}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => regler(t.id, q - 1)}
                    disabled={q === 0}
                    aria-label={`Une ${t.name} de moins`}
                    className="w-11 h-11 rounded-lg border-2 border-gray-300 text-xl font-bold text-gray-700 disabled:opacity-30"
                  >
                    −
                  </button>
                  <span className="w-8 text-center text-lg font-bold tabular-nums">{q}</span>
                  <button
                    type="button"
                    onClick={() => regler(t.id, q + 1)}
                    aria-label={`Une ${t.name} de plus`}
                    className="w-11 h-11 rounded-lg border-2 border-green-600 text-xl font-bold text-green-700"
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
          {total > 0 && (
            <p className={`text-green-800 font-semibold pt-1 ${compact ? 'text-[13px]' : ''}`}>
              Environ {euros(total)} crédités sur vos FOLIES après recomptage au retrait.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
